import {
	AttachmentFileType,
	Dtos,
	FileId,
	FinancialPeriodPreparationMethod,
	ISODate,
	PeriodType,
	SuretyAccountId,
} from "@inrev/common";
import { accountAttachmentsQueryKey } from "@portal/api/account";
import { useAttachmentUpload } from "@portal/api/bond-request/agent";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useAccountTabsNavigation } from "@portal/views/account/useAccountTabNavigation";
import { DateTime } from "luxon";
import { FC, useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { getNameAndExtensionFromFileName } from "../../utils";
import { stripEmptyResolver, typedZodResolver } from "../../utils/form";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "../form";
import { FormDatePicker, FormDropdown } from "../form/fields";
import { Modal } from "../layout/Modal";
import { ModalItemWithHeader } from "../layout/ModalItemWithHeader";
import { Button } from "../ui/Button";
import { Dropzone } from "../ui/Dropzone";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";

export type StatementCreateFormData = Partial<Dtos.Admin.FinancialPeriod.CreateForm.Request>;

// When provided, the create form submits through this config instead of the default
// upload-only path (e.g. to create a balance sheet or income statement).
export type StatementCreateConfig = {
	header: string;
	submitLabel: string;
	isLoading: boolean;
	error: Error | null;
	dismissError: () => void;
	submit: (formData: StatementCreateFormData, opts: { onSuccess: () => void }) => void;
};

// When provided, the modal renders the statement-keyed edit form (Period End Date,
// Period Type, Preparation Method) prefilled from `initialValues` and submits through
// this config instead of fetching an extraction (e.g. to edit a balance sheet or income
// statement period). Symmetric to `StatementCreateConfig`.
export type StatementEditConfig = {
	header: string;
	submitLabel: string;
	isLoading: boolean;
	error: Error | null;
	dismissError: () => void;
	initialValues: {
		periodEndDate?: ISODate;
		periodType?: PeriodType;
		preparationMethod?: FinancialPeriodPreparationMethod;
	};
	submit: (
		formData: Dtos.Admin.BalanceSheet.UpdatePeriod.Request,
		opts: { onSuccess: () => void },
	) => void;
};

export type CreateOrUpdatePeriodModalProps = {
	accountId: SuretyAccountId;
	onClose: () => void;
	createConfig?: StatementCreateConfig;
	editConfig?: StatementEditConfig;
};

export const CreateOrUpdatePeriodModal: FC<CreateOrUpdatePeriodModalProps> = ({
	accountId,
	onClose,
	createConfig,
	editConfig,
}) => {
	if (editConfig) {
		return <StatementEditPeriodModal editConfig={editConfig} onClose={onClose} />;
	}
	return <CreatePeriodModal accountId={accountId} onClose={onClose} createConfig={createConfig} />;
};

type StatementEditPeriodFormData = {
	periodEndDate?: ISODate;
	periodType?: PeriodType;
	preparationMethod?: FinancialPeriodPreparationMethod;
};

const StatementEditPeriodModal: FC<{
	editConfig: StatementEditConfig;
	onClose: () => void;
}> = ({ editConfig, onClose }) => {
	const schema = Dtos.Admin.BalanceSheet.UpdatePeriod.Request.schema;
	const defaultValues: StatementEditPeriodFormData = {
		periodEndDate: editConfig.initialValues.periodEndDate,
		periodType: editConfig.initialValues.periodType,
		preparationMethod: editConfig.initialValues.preparationMethod,
	};

	const { control, handleSubmit: onSubmit } = useForm<
		StatementEditPeriodFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		Dtos.Admin.BalanceSheet.UpdatePeriod.Request
	>({
		defaultValues,
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(typedZodResolver(schema)),
	});

	const handleEditSubmit: SubmitHandler<Dtos.Admin.BalanceSheet.UpdatePeriod.Request> = (data) => {
		editConfig.submit(data, { onSuccess: onClose });
	};

	return (
		<>
			<MutationErrorFlag
				errorHeading="Edit Period Error"
				mutationError={editConfig.error}
				dismissError={editConfig.dismissError}
			/>
			<Modal onClickOutside={onClose}>
				<ModalItemWithHeader header={editConfig.header} onClose={onClose}>
					<div className="flex flex-col min-w-[600px] max-w-[600px] pt-4 pb-8 px-8 min-h-[550px] max-h-[550px]">
						<div className="flex flex-col gap-4 py-2 overflow-y-visible">
							<div
								data-testid="period-modal-instructions"
								className="flex flex-col shrink-0 h-fit text-sm text-secondary items-start justify-start gap-2"
							>
								<div>Make changes to the period details below.</div>
							</div>
							<FormFieldGroup>
								<FormItemLabel condensed>Period End Date</FormItemLabel>
								<FormDatePicker
									name="periodEndDate"
									control={control}
									disableAfter={DateTime.now().toISODate()}
								/>
							</FormFieldGroup>
							<FormRow className="flex flex-col md:flex-row gap-2 shrink-0">
								<FormField>
									<FormItemLabel condensed>Period Type</FormItemLabel>
									<FormDropdown
										name="periodType"
										options={PeriodType.options()}
										placeholder="Select period type"
										control={control}
										anchor="bottom start"
									/>
								</FormField>
								<FormField>
									<FormItemLabel condensed>Preparation Method</FormItemLabel>
									<FormDropdown
										name="preparationMethod"
										options={FinancialPeriodPreparationMethod.options()}
										placeholder="Select preparation method"
										control={control}
										anchor="bottom start"
									/>
								</FormField>
							</FormRow>
						</div>
						<div className="flex flex-row-reverse items-center justify-start gap-2 mt-6">
							<Button
								testId="submit-button"
								className="order-1"
								color="primary"
								onClick={onSubmit(handleEditSubmit)}
								disabled={editConfig.isLoading}
								filled
							>
								{editConfig.submitLabel}
							</Button>
							<Button
								testId="cancel-button"
								className="order-2"
								color="secondary"
								onClick={onClose}
							>
								Cancel
							</Button>
						</div>
					</div>
				</ModalItemWithHeader>
			</Modal>
		</>
	);
};

const CreatePeriodModal: FC<{
	accountId: SuretyAccountId;
	onClose: () => void;
	createConfig?: StatementCreateConfig;
}> = ({ accountId, onClose, createConfig }) => {
	const { showErrorFlag, showSuccessFlag } = useFlags();
	const navigateTabs = useAccountTabsNavigation();

	const defaultValues: StatementCreateFormData = {
		periodEndDate: undefined,
		periodType: undefined,
		preparationMethod: undefined,
	};

	const { control, handleSubmit: onSubmit } = useForm<
		StatementCreateFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		Dtos.Admin.FinancialPeriod.CreateForm.Request
	>({
		defaultValues,
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver(Dtos.Admin.FinancialPeriod.CreateForm.Request.schema),
		),
	});

	const { uploadFn } = useAttachmentUpload<AttachmentFileType>(
		`/v2/admin/surety/accounts/${accountId}/attachments`,
		{
			onUploadSuccess: (_data) => {
				onClose();
				showSuccessFlag({
					heading: "Upload Successful",
					message: "Your file has been uploaded successfully.",
					actions: [
						{
							label: "View Upload Status",
							onClick: () => {
								navigateTabs({ tabId: "overview", subsectionId: "attachments" });
							},
						},
					],
				});
			},
			onUploadError: (error) => {
				showErrorFlag({
					heading: "Upload Error",
					message: error instanceof Error ? error.message : String(error),
				});
			},
			onUpdateSuccess: () => {},
			onUpdateError: () => {},
			onDeleteSuccess: () => {},
			onDeleteError: () => {},
		},
		[accountAttachmentsQueryKey(accountId)],
	);

	const handleUpload = useCallback(
		(dropZoneFiles: File[]) => {
			const fileUploads = dropZoneFiles.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: {
						id: v4() as FileId,
						name: fileName,
						extension,
						types: [],
					},
				};
			});
			uploadFn({ fileUploads });
		},
		[uploadFn],
	);

	const handleCreateSubmit: SubmitHandler<Dtos.Admin.FinancialPeriod.CreateForm.Request> = (
		data,
	) => {
		if (createConfig) {
			createConfig.submit(data, { onSuccess: onClose });
		}
	};

	const submitLoading = createConfig?.isLoading ?? false;
	const submitError = createConfig?.error ?? null;
	const dismissSubmitError = createConfig?.dismissError ?? (() => {});
	const header = createConfig?.header ?? "Add Custom Financial Period";
	const submitLabel = createConfig?.submitLabel ?? "Add Period";

	return (
		<>
			<MutationErrorFlag
				errorHeading={createConfig ? "Create Statement Error" : "Create Extraction Error"}
				mutationError={submitError}
				dismissError={dismissSubmitError}
			/>
			<Modal onClickOutside={onClose}>
				<ModalItemWithHeader header={header} onClose={onClose}>
					<div className="flex flex-col min-w-[600px] max-w-[600px] pt-4 pb-8 px-8 min-h-[550px] max-h-[550px]">
						<div className="flex flex-col gap-4 py-2 overflow-y-auto">
							<div
								data-testid="period-modal-instructions"
								className="flex flex-col shrink-0 h-fit text-sm text-secondary items-start justify-start gap-2"
							>
								<div>
									Create a new custom period by uploading corresponding financial documents or by
									manual input. If a custom period with this date already exists, it will be
									selected.
								</div>
								<Dropzone onSuccess={handleUpload} />
								<div className="flex w-full justify-center">-OR-</div>
							</div>
							<FormFieldGroup>
								<FormItemLabel condensed required>
									Period End Date
								</FormItemLabel>
								<FormDatePicker
									name="periodEndDate"
									control={control}
									disableAfter={DateTime.now().toISODate()}
								/>
							</FormFieldGroup>
							<FormRow className="flex flex-col md:flex-row gap-2 shrink-0">
								<FormField>
									<FormItemLabel condensed required>
										Period Type
									</FormItemLabel>
									<FormDropdown
										name="periodType"
										options={PeriodType.options()}
										control={control}
										anchor="bottom start"
									/>
								</FormField>
								<FormField>
									<FormItemLabel condensed required>
										Preparation Method
									</FormItemLabel>
									<FormDropdown
										name="preparationMethod"
										options={FinancialPeriodPreparationMethod.options()}
										control={control}
										anchor="bottom start"
									/>
								</FormField>
							</FormRow>
						</div>
						<div className="flex flex-row-reverse items-center justify-start gap-2 mt-6">
							<Button
								testId="submit-button"
								className="order-1"
								color="primary"
								onClick={onSubmit(handleCreateSubmit)}
								disabled={submitLoading}
								filled
							>
								{submitLabel}
							</Button>
							<Button
								testId="cancel-button"
								className="order-2"
								color="secondary"
								onClick={onClose}
							>
								Cancel
							</Button>
						</div>
					</div>
				</ModalItemWithHeader>
			</Modal>
		</>
	);
};
